import React from 'react';
import {
  Outlet,
  useLoaderData,
  useLocation,
  useNavigation,
} from 'react-router';
import { styled } from 'styled-components';
import { type AppPage, type AppView, isOperationPage } from './types/app';
import AppHeader from './components/AppHeader';
import FollowUpAppHeader from './components/FollowUpAppHeader';
import Loader from './components/Loader';
import StickyHeader from './components/StickyHeader';
import { mediaQueries } from './styles/mediaQueries';
import SelectedDetails from './components/SelectedDetails';
import { getAppPageFromPath } from './helpers/urlHelper';
import type { PageWrapperLoaderData } from './routers/loaders/pageWrapperLoader';
import ErrorModal from './components/ErrorModal';
import { IsLoadingPageContext, PageContext } from './context';
import { t } from 'i18next';

const Content = styled.div`
  padding: 0 1rem;
  display: block;
  margin-bottom: 1rem;

  ${mediaQueries.desktopUp} {
    padding: 0;
  }
`;

export const HydrateFallback = ({ view }: { view: AppView }) => {
  const { pathname } = useLocation();
  const page = getAppPageFromPath(pathname);

  if (page === 'questions') {
    return <Loader text={t('pages.questions.loadingQuestionnaire')} />;
  }

  const loadingTextKey =
    page === 'timeslots' || page === 'services'
      ? `components.loading.title.${page}`
      : 'components.loading.title.generic';

  if (isOperationPage(page)) {
    return (
      <>
        {view === 'alt' ? (
          <>
            <StickyHeader page={page} isLoading={true} />
            <Loader />
          </>
        ) : (
          <>
            <AppHeader page={page} />
            <Loader text={t(loadingTextKey)} />
          </>
        )}
      </>
    );
  }

  return <Loader />;
};

interface HeaderProps {
  operation?: string;
  page: AppPage;
  view: AppView;
  isLoading: boolean;
}

const Header = ({ operation, page, view, isLoading }: HeaderProps) => {
  const showFollowUpHeader =
    page === 'followup-timeslots' || page === 'followup-contact';

  if (showFollowUpHeader) {
    return <FollowUpAppHeader />;
  }

  if (!isOperationPage(page)) {
    return null;
  }

  if (!operation) {
    return null;
  }

  return (
    <>
      {view === 'alt' ? (
        <StickyHeader page={page} isLoading={isLoading} />
      ) : (
        <AppHeader page={page}>
          {page === 'contact' && (
            <Content>
              <SelectedDetails />
            </Content>
          )}
        </AppHeader>
      )}
    </>
  );
};

/**
 * Route-level layout wrapper mounted at the top of the route tree. Persists
 * across page navigation (not unmounted) and handles per-navigation
 * initialization such as syncing route params to application state. Renders
 * child routes via <Outlet />.
 */
const PageWrapper: React.FC<{ view: AppView }> = ({ view }) => {
  const { operation } = useLoaderData<PageWrapperLoaderData>();
  const currentLocation = useLocation();
  const { state, location: pendingLocation } = useNavigation();

  const isLoadingPage = state === 'loading';

  // During navigation, show target page in header; otherwise use current route
  const page = isLoadingPage
    ? getAppPageFromPath(pendingLocation?.pathname || '')
    : getAppPageFromPath(currentLocation.pathname);

  return (
    <>
      <Header
        operation={operation}
        page={page}
        view={view}
        isLoading={isLoadingPage}
      />
      <IsLoadingPageContext.Provider value={isLoadingPage}>
        <PageContext.Provider value={page}>
          <Outlet />
        </PageContext.Provider>
      </IsLoadingPageContext.Provider>
      <ErrorModal />
    </>
  );
};

export default PageWrapper;
